import {
  getRegularEnglishPluralForm,
  commaSeparateWords,
  pluralizeWord,
} from './stringHelpers'

export const formatCountLabel = (count, labelName) => {
  return `${count} ${getRegularEnglishPluralForm(count, labelName)}`
}

export const formatCountOfCountLabel = (count, total, label) => {
  return count === total
    ? `All ${getRegularEnglishPluralForm(2, label)}`
    : `${count} of ${total} ${getRegularEnglishPluralForm(2, label)}`
}

export const formatEmptyGradesLabel = grades => {
  const gradeNames = grades.map(g => g.gradeName)
  return `${commaSeparateWords(gradeNames)} ${
    grades.length > 1 ? "haven't" : "hasn't"
  } created classes yet.`
}

export const formatLastUpdatedRelativeTime = utcTimestamp => {
  if (!utcTimestamp) {
    return 'Updated just now'
  }

  const updatedAt = new Date(utcTimestamp)
  const currentUtcTimestamp = new Date()
  const timeDifferenceInMilliseconds =
    currentUtcTimestamp.getTime() - updatedAt.getTime()

  if (!Number.isFinite(timeDifferenceInMilliseconds)) {
    return 'Updated just now'
  }

  const totalMinutes = Math.floor(timeDifferenceInMilliseconds / (1000 * 60))

  if (totalMinutes <= 0) {
    return 'Updated just now'
  }

  const totalHours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  let copy = `Updated just now`

  // Less than 1 hour ago: show minutes
  if (totalHours === 0) {
    const minutesLabel = pluralizeWord(totalMinutes, 'minute', 'minutes')
    copy = `Updated ${totalMinutes} ${minutesLabel} ago`
  } else {
    // Round hours based on remaining minutes (> 30 rounds up)
    let roundedHours = totalHours

    if (minutes > 30) {
      roundedHours += 1
    }

    // One day or less: show only hours (previous behavior)
    if (roundedHours <= 24) {
      const hoursLabel = pluralizeWord(roundedHours, 'hour', 'hours')
      copy = `Updated ${roundedHours} ${hoursLabel} ago`
    } else {
      // More than one day: show days and remaining hours
      const days = Math.floor(roundedHours / 24)
      const remainingHours = roundedHours % 24

      const daysLabel = pluralizeWord(days, 'day', 'days')
      const hoursLabel = pluralizeWord(remainingHours, 'hour', 'hours')

      copy = `Updated ${days} ${daysLabel}, ${remainingHours} ${hoursLabel} ago`
    }
  }

  return copy
}

export const formatPageCount = (currentPage, numPages) => {
  return `Page ${currentPage} of ${numPages}`
}

export const formatProgress = (classesTaught, daysToReport, classesCount) => {
  return `**${formatCountOfCountLabel(
    classesTaught,
    classesCount,
    'class',
  )} ** marked a lesson done in the past ${daysToReport} days`
}

export const formatSchoolCounts = (
  classesCount,
  teachersCount,
  administratorsCount,
) => {
  const classesCountText = formatCountLabel(classesCount, 'Class')
  const teachersCountText = ` • ${formatCountLabel(teachersCount, 'Teacher')}`
  const administratorsCountText = ` • ${formatCountLabel(
    administratorsCount,
    'Administrator',
  )}`

  return `**${classesCountText}**${teachersCountText}${administratorsCountText}`
}

export const formatItemCount = (
  itemStartIndex,
  itemEndIndex,
  totalItems,
  label,
) =>
  `${itemStartIndex}–${itemEndIndex} of ${formatCountLabel(totalItems, label)}`

export const formatSiteCount = (siteStartIndex, siteEndIndex, maxSites) =>
  formatItemCount(siteStartIndex, siteEndIndex, maxSites, 'School')
